import { useCallback } from 'react';

import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

import {
  AccessMember,
  promotePrincipalAccess,
  removeAccess,
  removeGerant,
  setAccessOwner,
} from '../../api/personneMoraleAccessApi';

type CreateMenuItem = ReturnType<typeof useActionMenu>['createMenuItem'];

const toast = (message: string, color: GenericColors) =>
  toasterService.showToast({ message, displayType: DisplayTypeEnum.Toaster, color });

/**
 * Mutations partagées de la gestion d'équipe d'une personne morale (gérant,
 * principal, retrait), toasts inclus — consommées par la page « Mon équipe »
 * (portail) ET par CollaboratorsManager (back-office admin), pour que la même
 * opération ne diverge jamais entre les deux surfaces.
 */
export const useMemberTeamActions = (refresh: () => Promise<void> | void) => {
  const handleSetOwner = useCallback(
    async (member: AccessMember, isOwner: boolean) => {
      if (member.isOwner === isOwner) return;
      const res = await setAccessOwner(member.accessId, isOwner);
      if (!res.isSuccess) {
        toast(res.errorMessage ?? 'Échec du changement de gérant', GenericColors.Red);
        return;
      }
      toast(
        isOwner
          ? `${member.displayName} est maintenant gérant du cabinet.`
          : `${member.displayName} n'est plus gérant du cabinet.`,
        GenericColors.Green,
      );
      await refresh();
    },
    [refresh],
  );

  const handlePromote = useCallback(
    async (member: AccessMember) => {
      const res = await promotePrincipalAccess(member.accessId);
      if (!res.isSuccess) {
        toast(res.errorMessage ?? 'Échec de la promotion', GenericColors.Red);
        return;
      }
      toast(`${member.displayName} est maintenant le user principal.`, GenericColors.Green);
      await refresh();
    },
    [refresh],
  );

  /** Retire le membre ; true si le retrait a réussi (l'appelant ferme alors sa confirmation). */
  const removeMember = useCallback(
    async (member: AccessMember): Promise<boolean> => {
      const res = await removeAccess(member.accessId);
      if (!res.isSuccess) {
        toast(res.errorMessage ?? 'Échec du retrait', GenericColors.Red);
        return false;
      }
      toast(`${member.displayName} a été retiré.`, GenericColors.Green);
      await refresh();
      return true;
    },
    [refresh],
  );

  /** Removes the (legal) manager role: the collaborator is closed, the access stays. */
  const handleRemoveGerant = useCallback(
    async (member: AccessMember) => {
      const res = await removeGerant(member.accessId);
      if (!res.isSuccess) {
        toast(res.errorMessage ?? 'Échec du retrait du gérant', GenericColors.Red);
        return;
      }
      toast(`${member.displayName} ne signe plus les mandats du cabinet.`, GenericColors.Green);
      await refresh();
    },
    [refresh],
  );

  return { handleSetOwner, handlePromote, removeMember, handleRemoveGerant };
};

/**
 * "Signataire" badge: the manager in the legal sense, the one who signs the firm's
 * direct-debit mandates. Not to be confused with the "Gérant" badge of the neighbouring
 * column, which is the account owner (governance).
 */
export const MemberGerantCell: React.FC<{ member: AccessMember }> = ({ member }) =>
  member.isGerant ? (
    <span
      title={
        member.phone
          ? `Signe les mandats de prélèvement du cabinet · ${member.phone}`
          : 'Signe les mandats de prélèvement du cabinet · mobile à renseigner'
      }
      className={`rounded-full px-2 py-1 text-xs font-medium ${
        member.phone ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
      }`}
    >
      Signataire{member.phone ? '' : ' · mobile manquant'}
    </span>
  ) : (
    <span className="text-xs text-gray-400">—</span>
  );

/** Ready-to-use "Signataire des mandats" column for GenericTable. */
export const memberGerantColumn = {
  key: 'isGerant' as const,
  label: 'Signataire des mandats',
  render: (_: unknown, row: AccessMember) => <MemberGerantCell member={row} />,
};

/** Badge « Gérant » + étoile « Principal » — la colonne partagée des deux tableaux. */
export const MemberOwnerCell: React.FC<{ member: AccessMember }> = ({ member }) => (
  <div className="flex items-center gap-1.5">
    {member.isOwner ? (
      <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
        Gérant
      </span>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    )}
    {member.isPrincipal && (
      <span
        title="Utilisateur principal du cabinet"
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800"
      >
        ★ Principal
      </span>
    )}
  </div>
);

/** Colonne « Gérant » prête à l'emploi pour GenericTable. */
export const memberOwnerColumn = {
  key: 'isOwner' as const,
  label: 'Gérant',
  render: (_: unknown, row: AccessMember) => <MemberOwnerCell member={row} />,
};

/**
 * Shared menu entries (set principal, name/remove owner, declare/remove the mandates'
 * signatory) — each surface adds its own (bricks, removal…).
 */
export const buildBaseMemberMenuItems = (
  createMenuItem: CreateMenuItem,
  member: AccessMember,
  actions: {
    onPromote: (member: AccessMember) => void;
    onToggleOwner: (member: AccessMember, isOwner: boolean) => void;
    onDeclareGerant: (member: AccessMember) => void;
    onRemoveGerant: (member: AccessMember) => void;
  },
) => [
  createMenuItem(
    'promote-principal',
    'Définir comme principal',
    () => actions.onPromote(member),
    {
      icon: 'star',
      disabled: member.isPrincipal || !member.isOwner,
    },
  ),
  createMenuItem(
    member.isOwner ? 'demote-owner' : 'promote-owner',
    member.isOwner ? 'Retirer la gestion du cabinet' : 'Nommer gérant du cabinet',
    () => actions.onToggleOwner(member, !member.isOwner),
    { icon: 'user' },
  ),
  // Only an active access can become the mandates' signatory: DeclareGerantPanel
  // filters inactive members out, so the entry must not offer them.
  createMenuItem(
    member.isGerant ? 'remove-gerant' : 'declare-gerant',
    member.isGerant ? 'Retirer le rôle de signataire des mandats' : 'Déclarer signataire des mandats',
    () => (member.isGerant ? actions.onRemoveGerant(member) : actions.onDeclareGerant(member)),
    { icon: 'edit', disabled: !member.isGerant && !member.isActive },
  ),
];
